import type { IPartFilter, IPartSortByOption } from "@contentful-api/types/contentful";
import type { Facets } from "@store/index";
import { useStore } from "@store/index";
import { useFilterStore } from "@store/filterStore";
import type { FC, ReactNode } from "react";
import { useEffect, useCallback, useMemo } from "react";
import { type ConfigureProps, useClearRefinements, useConfigure } from "react-instantsearch";
import { FiltersContext } from "./FiltersContext";
import { DEFAULT_ATTRIBUTES_TO_RETRIEVE } from "@lib/algolia/constants";
import { AlgoliaProductProperties } from "@lib/wrappers/algolia/types";
import { INITIAL_HITS_PER_PAGE } from "@components/commerce/product/list/product-list-footer/ProductListFooter";

interface IFiltersControllerProps {
    children: ReactNode;
    listOfFilters: IPartFilter[];
    sortByOptionsList: IPartSortByOption[];
    isMenPage?: boolean;
    pageCategory?: string;
    page?: number;
}

export const FiltersController: FC<IFiltersControllerProps> = ({
    children,
    listOfFilters,
    sortByOptionsList,
    isMenPage = true,
    pageCategory,
    page = 1,
}) => {
    // Algolia configuration (unchanged)
    useConfigure({
        filters: pageCategory ? `categories:"${pageCategory}" AND hasStock:true` : "hasStock:true",
        hitsPerPage: INITIAL_HITS_PER_PAGE * page,
        facetingAfterDistinct: true,
        attributesToRetrieve: [
            ...DEFAULT_ATTRIBUTES_TO_RETRIEVE,
            AlgoliaProductProperties.descriptiveHeading,
        ],
        page: 0,
    } as ConfigureProps);

    // Global store access (unchanged)
    const globalShoeSizeConvention = useStore((state) => state.data.me?.globalShoeSizeConvention);
    const setGlobalShoeSizeConvention = useStore((state) => state.setGlobalShoeSizeConvention);

    // Static refinement function
    const { refine: clearRefinements } = useClearRefinements();

    // Initialize filter map in store if needed
    const initializeFilters = useCallback(() => {
        const filtersMap: Facets = {};
        listOfFilters?.forEach((filter) => {
            filtersMap[filter?.fields?.filterName] = { refine: null, items: [] };
        });
        useFilterStore.getState().setFilterItems(filtersMap);
    }, [listOfFilters]);

    // Initialize filters on mount
    useEffect(() => {
        initializeFilters();
    }, [initializeFilters]);

    // Simplified context value - only static configuration
    const contextValue = useMemo(
        () => ({
            // Static configuration values
            listOfFilters,
            sortByOptionsList,
            isMenPage,

            // Static methods
            clearRefinements,

            // Size locale (could be moved to separate context if needed)
            sizeLocale: globalShoeSizeConvention,
            setSizeLocale: setGlobalShoeSizeConvention,
        }),
        [
            listOfFilters,
            sortByOptionsList,
            isMenPage,
            clearRefinements,
            globalShoeSizeConvention,
            setGlobalShoeSizeConvention,
        ]
    );

    return <FiltersContext.Provider value={contextValue}>{children}</FiltersContext.Provider>;
};
